import MainLayout from 'Components/Layout/MainLayout';
import ProForm from 'Components/ProForm';
import {Badge, Button, Input, message, Modal, Space, Tag} from 'antd';
import ProTable, {ProTableColumnsType} from 'Components/ProTable';
import {
    ExportOutlined,
    ImportOutlined,
    UserAddOutlined,
    UserOutlined,
} from '@ant-design/icons';
import React, {useState} from 'react';
import useRequest from 'Utils/Hooks/useRequest';
import {
    changeUserStatus,
    deleteUser,
    exportUserExcel,
    GenderDict,
    getUserListPage,
    QueryUserList,
    resetUserPwd,
    UserStatus,
    UserStatusDict,
} from './service';
import usePagination from 'Utils/Hooks/usePagination';
import SelectDict from 'Features/Select/SelectDict';
import useModalHandler from 'Utils/Hooks/useModalHandler';
import {UserModal} from './Components/UserModal';
import {useDict} from './store';
import {errorMessage} from 'Utils/common';
import {UserImportModal} from './Components/UserImportModal';
import useRequestFn from 'Utils/Hooks/useRequestFn';
import ReactDOM from 'react-dom';
import {UserListItem} from 'Services/common';
import useAuth from 'Utils/Hooks/useAuth';
import Style from './index.module.scss';
import ImportLog from '../../../Components/ImportLog';
import {Auth} from '../../../Features/Auth';
import dayjs from 'dayjs';

export default function UserManage() {
    const dict = useDict();
    const auth = useAuth();
    const userModal = useModalHandler({component: UserModal});
    const userImportModal = useModalHandler({component: UserImportModal});

    const [query, setQuery] = useState<QueryUserList>({});
    const {params, tableProps, gotoPage} = usePagination(20);

    const [data, {loading}, refresh] = useRequest(
        () => getUserListPage(query, params),
        {},
        [params, query],
    );

    function onSearch(query: QueryUserList) {
        ReactDOM.unstable_batchedUpdates(() => {
            setQuery(query);
            gotoPage(1);
        });
    }

    function onFreeze(id: string, currentStatus: UserStatus) {
        const status =
            currentStatus === UserStatus.Normal
                ? UserStatus.Freeze
                : UserStatus.Normal;

        const noticeFreeze =
            '被冻结的账号将无法正常登录平台, 你确定要冻结该账号吗?';

        const noticeNormal =
            '恢复正常成功后, 该账号可以正常登录平台, 你确定要将该账号恢复正常吗?';

        const notice =
            status === UserStatus.Freeze ? noticeFreeze : noticeNormal;

        Modal.confirm({
            content: notice,
            onOk: async () => {
                try {
                    await changeUserStatus(id, status);
                    message.success('操作成功!');
                    refresh();
                } catch (e) {
                    message.error(errorMessage(e));
                }
            },
        });
    }

    function onDelete(id: string) {
        Modal.confirm({
            content: '删除后不可恢复, 你确定要删除该账号吗?',
            onOk: async () => {
                try {
                    await deleteUser(id);
                    message.success('删除成功!');
                    refresh();
                } catch (e) {
                    message.error(errorMessage(e));
                }
            },
        });
    }

    function onResetPwd(id: string) {
        Modal.confirm({
            content: '确定要重置该账号的密码吗?',
            onOk: async () => {
                try {
                    await resetUserPwd(id);
                    message.success('重置成功!');
                } catch (e) {
                    message.error(errorMessage(e));
                }
            },
        });
    }

    const [onExport, exportStatus] = useRequestFn(async () => {
        try {
            await exportUserExcel();
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    function onImportOk() {
        userImportModal.close();
        refresh();
    }

    function onSaveOk() {
        userModal.close();
        refresh();
    }

    const columns: ProTableColumnsType<UserListItem> = [
        {title: '用户账号', dataIndex: 'loginName'},
        {title: '用户名', dataIndex: 'userName'},
        {
            title: '性别',
            dataIndex: 'gender',
            render: gender => (gender === '1' ? '男' : '女'),
        },
        {title: '手机号码', dataIndex: 'phone'},
        {title: '座机', dataIndex: 'tel'},
        {
            title: '状态',
            dataIndex: 'status',
            render: (status: UserStatus) => (
                <Badge
                    status={
                        status === UserStatus.Normal ? 'success' : 'default'
                    }
                    text={UserStatusDict.getName(status)}
                />
            ),
        },
        {
            title: '角色',
            dataIndex: 'roleId',
            width: 200,
            render: (roleId: string[], item) =>
                roleId?.map(roleId => (
                    <Tag key={roleId}>
                        {item.defaultRoleId === roleId && <UserOutlined />}
                        {dict.RoleList.getName(roleId)}
                    </Tag>
                )),
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            render: (createTime: string) =>
                dayjs(createTime).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '操作',
            dataIndex: 'id',
            align: 'center',
            width: 300,
            fixed: 'right',
            render: (id: string, item) => (
                <ProTable.Operate>
                    {auth('SF_J476Zfxw08') && (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => onResetPwd(id)}>
                            重置密码
                        </Button>
                    )}

                    {auth('SF_J476Zfxw03') && (
                        <Button
                            size="small"
                            type="link"
                            onClick={() =>
                                userModal.open({id, onOk: onSaveOk})
                            }>
                            编辑
                        </Button>
                    )}

                    {auth('SF_J476Zfxw09') && (
                        <Button
                            size="small"
                            type="link"
                            onClick={() => onFreeze(id, item.status)}
                            disabled={item.loginName === 'admin'}>
                            {item.status === UserStatus.Freeze
                                ? '恢复正常'
                                : '冻结'}
                        </Button>
                    )}

                    {auth('SF_J476Zfxw04') && (
                        <Button
                            size="small"
                            type="link"
                            onClick={() => onDelete(id)}
                            disabled={item.loginName === 'admin'}>
                            删除
                        </Button>
                    )}
                </ProTable.Operate>
            ),
        },
    ];

    const toolBar = (
        <div className={Style.toolBar}>
            <Space>
                {auth('SF_J476Zfxw02') && (
                    <Button
                        type="primary"
                        icon={<UserAddOutlined />}
                        onClick={() => userModal.open({onOk: onSaveOk})}>
                        添加用户
                    </Button>
                )}

                {auth('SF_J476Zfxw05') && (
                    <Button
                        type="primary"
                        ghost
                        icon={<ImportOutlined />}
                        onClick={() =>
                            userImportModal.open({onOk: onImportOk})
                        }>
                        导入
                    </Button>
                )}

                {auth('SF_J476Zfxw07') && (
                    <Button
                        type="primary"
                        ghost
                        icon={<ExportOutlined />}
                        onClick={onExport}
                        loading={exportStatus.loading}>
                        导出Excel
                    </Button>
                )}
            </Space>

            <Auth code="SF_J476Zfxw06">
                <ImportLog
                    logUrl="/user/getImportErrorPage"
                    downloadUrl="/user/exportImportError"
                />
            </Auth>
        </div>
    );

    return (
        <MainLayout.SpaceWrapper padding>
            <ProForm.Query onFinish={onSearch}>
                <ProForm.Field name="userName" label="用户名">
                    <Input placeholder="输入账号模糊查询" />
                </ProForm.Field>

                <ProForm.Field name="gender" label="性别">
                    <SelectDict allowClear options={GenderDict.data} />
                </ProForm.Field>
            </ProForm.Query>

            <ProTable
                rowKey="id"
                columns={columns}
                dataSource={data?.data}
                toolBar={toolBar}
                options={false}
                {...tableProps(data?.total)}
                loading={loading}
            />

            {userModal.render()}
            {userImportModal.render()}
        </MainLayout.SpaceWrapper>
    );
}
